import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'product';
  noindex?: boolean;
  jsonLd?: object;
}

export default function SEO({ 
  title, 
  description, 
  canonical,
  ogImage = '/og-image-final.png',
  ogType = 'website',
  noindex = false,
  jsonLd
}: SEOProps) {
  const siteUrl = 'https://facefable-app-glow.lovable.app';
  const fullTitle = `${title} | FaceFable™ - AI-Powered Skincare Journey`;
  const canonicalUrl = canonical || `${siteUrl}${window.location.pathname}`;

  // Default JSON-LD Organization schema
  const defaultJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "FaceFable™",
    "url": siteUrl,
    "logo": `${siteUrl}/facefable-logo.png`,
    "description": "AI-powered skincare journey with personalised routines, face scanning, and community support",
    "sameAs": [
      "https://twitter.com/facefable",
      "https://facebook.com/facefable"
    ]
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="FaceFable™" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Additional Meta */}
      <meta name="theme-color" content="#6366f1" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(jsonLd || defaultJsonLd)}
      </script>
    </Helmet>
  );
}
